import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import logoBlueLong from "../assets/brand/logos/AzulOscuroLargo.png";
import logoBlueMedium from "../assets/brand/logos/AzulOscuroMedio.webp";
import logoBlueShort from "../assets/brand/logos/AzulOscuroCorto.png";
import loginBackground from "../assets/brand/backgrounds/DG2.webp";
import loginBackgroundMobile from "../assets/brand/backgrounds/DG2Mobile.webp";
import { ResetPasswordForm } from "../components/auth/ResetPasswordForm";
import { FormCard } from "../components/form/FormCard";
import { BrandButton } from "../components/ui/BrandButton";
import { verifyPasswordResetToken } from "../services/authService";
import { useAppLoading } from "../context/AppLoadingContext";
import { useTrackedImagePreload } from "../hooks/useTrackedImagePreload";
import { brandFormCardClass } from "../components/form/fieldStyles";
import { TrackedImage } from "../components/ui/TrackedImage";

export function ResetPasswordPage() {
  const { trackAsync } = useAppLoading();
  useTrackedImagePreload(loginBackground);
  useTrackedImagePreload(loginBackgroundMobile);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [status, setStatus] = useState("loading");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    let active = true;

    async function verifyToken() {
      if (!token) {
        if (active) {
          setStatus("invalid");
          setErrorMessage("El enlace no es válido. Pedí uno nuevo desde el formulario de recuperación.");
        }
        return;
      }

      try {
        await verifyPasswordResetToken(token);
        if (active) {
          setStatus("ready");
        }
      } catch (error) {
        if (active) {
          setStatus("invalid");
          setErrorMessage(error.message || "El enlace no es válido. Pedí uno nuevo.");
        }
      }
    }

    void trackAsync(verifyToken);

    return () => {
      active = false;
    };
  }, [token, trackAsync]);

  useEffect(() => {
    if (status !== "success") {
      return undefined;
    }

    const timeout = window.setTimeout(() => {
      navigate("/", { replace: true, preventScrollReset: true });
    }, 1800);

    return () => window.clearTimeout(timeout);
  }, [navigate, status]);

  return (
    <div
      className="auth-background min-h-screen overflow-hidden text-brand-primary"
      style={{
        "--auth-background-image": `url(${loginBackground})`,
        "--auth-background-mobile-image": `url(${loginBackgroundMobile})`,
      }}
    >
      <main className="flex min-h-screen w-full flex-col">
        <header className="flex items-start justify-start px-[40px] pt-[40px] sm:justify-between sm:px-[44px] sm:pt-[44px]">
          <TrackedImage
            src={logoBlueLong}
            alt="Penca OSU"
            className="h-[52px] w-auto object-contain sm:hidden"
          />
          <TrackedImage
            src={logoBlueMedium}
            alt="Penca OSU"
            className="hidden h-[52px] w-auto object-contain sm:block sm:h-[72px]"
          />
          <TrackedImage
            src={logoBlueShort}
            alt="Penca OSU"
            className="hidden h-[52px] w-auto object-contain sm:block sm:h-[72px]"
          />
        </header>

        <div className="flex flex-1 items-center justify-center px-4 py-6 sm:px-6 sm:py-10 lg:px-8 lg:py-8">
          <section className="w-full max-w-3xl">
            <div className="mb-4 flex justify-end">
              <BrandButton
                as={Link}
                to="/"
                preventScrollReset
                variant="outline"
                radius="xl"
                size="md"
                motion="subtle"
              >
                Volver al login
              </BrandButton>
            </div>

            {status === "loading" ? (
              <FormCard className={brandFormCardClass}>
                <div className="flex flex-col items-center gap-3 py-8 text-center text-slate-600">
                  <TrackedImage
                    src={logoBlueLong}
                    alt="Organización Sionista del Uruguay"
                    className="h-auto w-[250px] max-w-full"
                  />
                  <p className="text-sm font-medium">Verificando enlace...</p>
                </div>
              </FormCard>
            ) : null}

            {status === "invalid" ? (
              <FormCard className={brandFormCardClass}>
                <div className="flex flex-col items-center text-center">
                  <TrackedImage
                    src={logoBlueLong}
                    alt="Organización Sionista del Uruguay"
                    className="h-auto w-[250px] max-w-full"
                  />
                  <h2 className="mt-5 font-display text-3xl text-brand-primary sm:text-4xl">
                    Enlace no válido
                  </h2>
                  <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-600 sm:text-base">
                    {errorMessage}
                  </p>
                  <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                    <BrandButton
                      as={Link}
                      to="/forgot-password"
                      variant="primary"
                      radius="full"
                      size="lg"
                    >
                      Pedir otro enlace
                    </BrandButton>
                    <BrandButton
                      as={Link}
                      to="/"
                      preventScrollReset
                      variant="outline"
                      radius="xl"
                      size="lg"
                      motion="subtle"
                    >
                      Volver al login
                    </BrandButton>
                  </div>
                </div>
              </FormCard>
            ) : null}

            {status === "ready" ? (
              <ResetPasswordForm
                token={token}
                logoSrc={logoBlueLong}
                onSuccess={(message) => {
                  setSuccessMessage(message);
                  setStatus("success");
                }}
              />
            ) : null}

            {status === "success" ? (
              <FormCard className={brandFormCardClass}>
                <div className="flex flex-col items-center text-center">
                  <TrackedImage
                    src={logoBlueLong}
                    alt="Organización Sionista del Uruguay"
                    className="h-auto w-[250px] max-w-full"
                  />
                  <h2 className="mt-5 font-display text-3xl text-brand-primary sm:text-4xl">
                    Contraseña actualizada
                  </h2>
                  <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-600 sm:text-base">
                    {successMessage || "Ya podés iniciar sesión con tu nueva contraseña."}
                  </p>
                  <p className="mt-4 text-sm text-slate-500">
                    Te estamos redirigiendo al login.
                  </p>
                </div>
              </FormCard>
            ) : null}
          </section>
        </div>
      </main>
    </div>
  );
}
